import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Avatar,
} from '@mui/material';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import AddIcon          from '@mui/icons-material/Add';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';

import { useGetCampsByUserQuery } from '../../redux/services/camp-api';
import { useAuthContext } from '../../context/auth-context';
import { CreateCampForm } from '../camp/create-form';
import { useIsMobile } from '../../hooks/use-is-mobile';

/**
 * CampSelectDialog
 * Props:
 *   open        {boolean}   — controla visibilidad
 *   onClose     {function}  — llamado cuando el usuario selecciona un camp o cierra
 *   disableClose {boolean}  — si true, no se puede cerrar sin seleccionar (primer login)
 */
function CampSelectDialog({ open, onClose, disableClose = false }) {
  const { user, activeCamp, setActiveCamp } = useAuthContext();
  const isMobile = useIsMobile();
  const userId = user?._id;

  const { data: rawCamps = [], isLoading, isError } = useGetCampsByUserQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });
  const camps = rawCamps.filter((c) => c.status === true);

  const [showCreateForm, setShowCreateForm] = useState(false);

  const handleSelect = (camp) => {
    setActiveCamp(camp);
    setShowCreateForm(false);
    onClose();
  };

  const handleCampCreated = (newCamp) => {
    // Auto-select the newly created camp and close
    if (newCamp?._id) {
      setActiveCamp(newCamp);
      setShowCreateForm(false);
      onClose();
    }
  };

  const noCamps = !isLoading && !isError && camps.length === 0;

  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2, overflow: 'hidden' },
      }}
    >
      {/* Cabecera verde */}
      <Box sx={{ backgroundColor: '#284B32', px: 3, py: 2.5 }}>
        <DialogTitle
          sx={{
            p: 0,
            color: 'white',
            fontWeight: 700,
            fontSize: '1.1rem',
          }}
        >
          {showCreateForm ? 'Nuevo Campamento' : 'Seleccionar Campamento'}
        </DialogTitle>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
          {showCreateForm
            ? 'Completa los datos del campamento'
            : disableClose
            ? 'Elige el campamento con el que deseas trabajar'
            : 'Cambia el campamento activo'}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* ── Loading ─────────────────────────────────────────────────── */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#284B32' }} />
          </Box>
        )}

        {/* ── Error ───────────────────────────────────────────────────── */}
        {isError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">No se pudieron cargar los campamentos.</Alert>
          </Box>
        )}

        {/* ── Formulario de creación (inline) ─────────────────────────── */}
        {showCreateForm && (
          <Box sx={{ px: 3, py: 2 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setShowCreateForm(false)}
              sx={{ mb: 2, color: 'text.secondary' }}
            >
              Volver
            </Button>
            <CreateCampForm
              handleClose={() => setShowCreateForm(false)}
              onSuccess={handleCampCreated}
            />
          </Box>
        )}

        {/* ── Lista de campamentos ─────────────────────────────────────── */}
        {!isLoading && !isError && !showCreateForm && (
          <>
            {camps.length > 0 && (
              <List disablePadding>
                {camps.map((camp) => {
                  const isActive = activeCamp?._id === camp._id;
                  return (
                    <ListItemButton
                      key={camp._id}
                      onClick={() => handleSelect(camp)}
                      sx={{
                        py: 1.5,
                        px: 3,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        backgroundColor: isActive ? 'rgba(40,75,50,0.07)' : 'transparent',
                        '&:hover': { backgroundColor: 'rgba(40,75,50,0.1)' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Avatar
                          src={camp.logoUrl || undefined}
                          sx={{ width: 28, height: 28, bgcolor: isActive ? '#284B32' : 'action.selected' }}
                        >
                          {!camp.logoUrl && (
                            <NaturePeopleIcon fontSize="small" sx={{ color: isActive ? '#fff' : 'text.secondary' }} />
                          )}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={camp.name}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 700 : 400,
                          color: isActive ? '#284B32' : 'text.primary',
                          fontSize: '0.95rem',
                        }}
                      />
                      {isActive && (
                        <CheckCircleIcon fontSize="small" sx={{ color: '#284B32' }} />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            )}

            {/* ── Sin campamentos: mensaje + botón crear ───────────────── */}
            {noCamps && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <NaturePeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  No tienes campamentos asignados.
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 2.5 }}>
                  Crea uno nuevo para comenzar.
                </Typography>
              </Box>
            )}

            {/* ── Botón crear (siempre visible al fondo) ───────────────── */}
            <Divider />
            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                variant={noCamps ? 'contained' : 'outlined'}
                startIcon={<AddIcon />}
                onClick={() => setShowCreateForm(true)}
                sx={noCamps ? {
                  backgroundColor: '#284B32',
                  '&:hover': { backgroundColor: '#1e3826' },
                } : {
                  borderColor: '#284B32',
                  color: '#284B32',
                  '&:hover': { borderColor: '#1e3826', backgroundColor: 'rgba(40,75,50,0.05)' },
                }}
              >
                Crear Campamento
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CampSelectDialog;
